import React, { useContext, useEffect, useMemo } from 'react';

// DEPENDENCIES
import trim from 'lodash/trim';
import { useFormikContext } from 'formik';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';

// HELPERS
import { RecordContext } from 'helpers/getRecordData';
import { useDetail } from '../../helpers/DetailProvider';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';

// LOCAL COMPONENTS
import Card from '../../parts/Card';
import SaveField from '../../../../tools/SaveField';
import RegistrationAddress from './parts/RegistrationAddress';

// STYLES
import styles from './dealDetails.module.scss';

// VARIABLES
const STATES_OPTION_KEYS = {
  label: 'state_name',
  value: 'state_code'
};

// MAIN COMPONENT
const DealDetailsForm = ({ number, onCompleted, readOnly }) => {
  const { setCompleted, isDisabled } = useDetail();

  const { record: initialValues = {}, refetchRecord } = useContext(RecordContext) || {};
  const { values = {}, setFieldValue } = useFormikContext() || {};

  useEffect(() => {
    const { customer_number, tag_state_code, deal_number } = initialValues;
    if (customer_number) setFieldValue('customer_number', customer_number);
    if (tag_state_code) setFieldValue('tag_state_code', tag_state_code);
    if (deal_number) setFieldValue('deal_number', deal_number);
  }, [initialValues, setFieldValue]);

  useEffect(() => {
    const { sales_id, deal_number, customer_number, tag_state_code } = initialValues;
    setCompleted(number, !!sales_id && !!deal_number && !!customer_number && !!tag_state_code);
  }, [initialValues, number, setCompleted]);

  const { sales_id, deal_number, customer_number, tag_state_code } = values;

  const dealNumber = trim(deal_number);
  const customerNumber = trim(customer_number);

  const disabled = !readOnly && isDisabled(number);

  const disableButton = useMemo(
    () => !(dealNumber && customerNumber && tag_state_code),
    [customerNumber, dealNumber, tag_state_code]
  );

  return (
    <Form.Body>
      <SaveField
        useButton
        endpoint={ENDPOINTS.amFinance.dealDetails.save}
        params={{
          sales_id,
          deal_number: dealNumber,
          customer_number: customerNumber,
          tag_state_code
        }}
        label="Deal Details"
        onSuccess={async () => {
          refetchRecord({
            onComplete: () => onCompleted({ nextNumber: number + 1 })
          });
        }}
        plaintext={readOnly}
        disableButton={disableButton}
      >
        <Form.Control
          name="customer_number"
          label="Customer Number"
          input={{
            maxLength: 50
          }}
          disabled={disabled}
        />
        <Form.Select
          name="tag_state_code"
          label="Tag State"
          lookup="states"
          optionKeys={STATES_OPTION_KEYS}
          dropdown={{
            maxHeight: '30rem',
            popover: {
              className: styles.popover
            }
          }}
          disabled={disabled}
        />
        <RegistrationAddress {...values} />
        <Form.Control
          name="deal_number"
          label="Deal Number"
          input={{
            maxLength: 50
          }}
          disabled={disabled}
        />
      </SaveField>
    </Form.Body>
  );
};

// EXPORT
export default function DealDetails(props) {
  const { record: initialValues = {} } = useContext(RecordContext) || {};

  const { number, readOnly } = props;

  return (
    <Card
      number={number}
      title="Deal Details"
      readOnly={readOnly}
      body={
        <Form initialValues={initialValues}>
          <DealDetailsForm {...props} />
        </Form>
      }
    />
  );
}
